import React from 'react';
import { useTranslation } from 'react-i18next';

interface PageExtrasContent {
  title: string;
  intro?: string;
  sections: { title: string; body: string }[];
}

interface PageExtrasProps {
  /** Key under `pageExtras` in locales/<lang>/common.json. */
  pageKey: string;
}

/**
 * Plain, readable background copy at the foot of a page: answers the questions a visitor (or a search
 * or AI crawler) has after reading the page's main content. Text lives in the locale files so the
 * English and Spanish pages stay in step. Renders nothing if the key is missing for a language.
 */
const PageExtras: React.FC<PageExtrasProps> = ({ pageKey }) => {
  const { t } = useTranslation('common');
  const content = t(`pageExtras.${pageKey}`, { returnObjects: true, defaultValue: null }) as PageExtrasContent | null;

  if (!content || typeof content !== 'object' || !Array.isArray(content.sections)) return null;

  return (
    <section className="py-16 border-t border-white/10" aria-labelledby={`page-extras-${pageKey}`}>
      <div className="mx-auto max-w-4xl px-6">
        <h2 id={`page-extras-${pageKey}`} className="text-3xl md:text-4xl font-serif font-bold text-white">{content.title}</h2>
        {content.intro && <p className="mt-6 text-lg leading-relaxed text-white/80">{content.intro}</p>}
        <div className="mt-10 grid gap-10 md:grid-cols-2">
          {content.sections.map(section => (
            <div key={section.title}>
              <h3 className="text-xl font-semibold text-brand-gold-light">{section.title}</h3>
              <p className="mt-3 leading-relaxed text-white/80">{section.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PageExtras;
